import { Loading, Spinner, Text } from '@cubeyou-team/hx-react-components';
import classNames from 'classnames';
import React, { FC, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import { AI_GRADIENT_PROPERTIES } from '@discover-frontend/constants';

import translations from 'src/lang';
import { usePanelHeightResize } from '../../../../hooks/use-panel-resize/usePanelHeightResize';
import {
  StanleyQueryPanelContainer,
  StanleyQueryPanelHeader,
} from './components';
import {
  PANEL_VIEWS,
  StanleyPanelView,
  StanleyQueryPanelProvider,
  useStanleyQueryPanel,
} from './providers/StanleyQueryPanelContext';

const { STANLEY_QUERY: translation } = translations['en-US'];

const INITIAL_PANEL_WIDTH_PCT = 48;
const MIN_PANEL_WIDTH_PX = 240;
const MIN_PANEL_HEIGHT_PX = 112;
const CREATE_AUDIENCE_MODAL_ROW_ID = 'gtCreateAudienceModalRow';

const getResizeHost = (panel: HTMLElement | null) =>
  (panel?.offsetParent as HTMLElement | null) ||
  (typeof document !== 'undefined'
    ? document.getElementById(CREATE_AUDIENCE_MODAL_ROW_ID)
    : null);

const getCollapsedHeightPct = (panel: HTMLElement | null) => {
  const host = getResizeHost(panel);
  if (!host) return 100;

  return (MIN_PANEL_HEIGHT_PX / host.getBoundingClientRect().height) * 100;
};

const StyledStanleyQueryPanel = styled.div<{
  $panelWidthPct: number;
  $panelHeightPct: number;
}>`
  z-index: 31;
  border-left: 2px solid ${(props) => props.theme.colors.light_darker_1};
  box-shadow: -4px 0 16px rgba(25, 25, 39, 0.08);
  width: ${(props) => props.$panelWidthPct}%;
  min-width: ${MIN_PANEL_WIDTH_PX}px;
  height: ${(props) => props.$panelHeightPct}%;
  min-height: ${MIN_PANEL_HEIGHT_PX}px;
`;

const StanleyQueryPanelExpandedBody: FC = () => {
  const { isLoading, isFetchingHistoryItem } = useStanleyQueryPanel();

  return (
    <div className="MK1 flex min-h-0 flex-1 flex-col bg-hx-light-dark">
      <Loading
        isLoading={isLoading || isFetchingHistoryItem}
        loadingElement={
          <div className="flex h-full w-full flex-1 flex-col items-center justify-center">
            <Spinner gradient={AI_GRADIENT_PROPERTIES} />
            <Text size="headline" align="center">
              {translation.MODAL.LOADING}
            </Text>
          </div>
        }
      >
        <StanleyQueryPanelContainer />
      </Loading>
    </div>
  );
};

export interface StanleyQueryPanelProps {
  onClose: () => void;
  initialView?: StanleyPanelView;
  openHistoryRequestId?: number;
  onViewChange?: (view: StanleyPanelView) => void;
}

export const StanleyQueryPanel: FC<StanleyQueryPanelProps> = ({
  onClose,
  initialView = PANEL_VIEWS.FORM,
  openHistoryRequestId = 0,
  onViewChange,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(true);

  const { panelHeightPct, setPanelHeightPct, handleResizePointerDown } =
    usePanelHeightResize(true, panelRef, {
      handleEdge: 'top',
      initialPanelHeightPct: 100,
    });

  const handleToggleExpanded = () => {
    if (expanded) {
      setPanelHeightPct(getCollapsedHeightPct(panelRef.current));
      setExpanded(false);
      return;
    }

    setPanelHeightPct(100);
    setExpanded(true);
  };

  const handlePanelResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!expanded) {
        setExpanded(true);
      }
      handleResizePointerDown(e);
    },
    [expanded, handleResizePointerDown]
  );

  return (
    <StyledStanleyQueryPanel
      ref={panelRef}
      data-test-id="stanley-query-panel"
      $panelWidthPct={INITIAL_PANEL_WIDTH_PCT}
      $panelHeightPct={panelHeightPct}
      className="absolute right-0 bottom-0 top-auto flex min-h-0 max-w-full flex-col overflow-hidden bg-white"
    >
      <StanleyQueryPanelProvider
        initialView={initialView}
        openHistoryRequestId={openHistoryRequestId}
        onViewChange={onViewChange}
      >
        <div
          className={classNames(
            'absolute left-0 top-0 z-20 box-border h-2 w-full cursor-row-resize select-none hover:bg-custom-ai-panel-hover',
            'touch-none'
          )}
          data-test-id="stanley-query-panel-resize-handle"
          title="Resize panel height"
          onPointerDown={handlePanelResizePointerDown}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize Stanley Query panel height"
        />
        <div
          className={classNames('flex flex-col', {
            'relative h-full min-h-0 flex-1': expanded,
            'h-auto flex-shrink-0 flex-grow-0': !expanded,
          })}
        >
          <StanleyQueryPanelHeader
            expanded={expanded}
            onToggleExpanded={handleToggleExpanded}
            onClose={onClose}
          />
          {expanded && <StanleyQueryPanelExpandedBody />}
        </div>
      </StanleyQueryPanelProvider>
    </StyledStanleyQueryPanel>
  );
};
